import { t } from '@transifex/native';

import DateTime from 'pkg/datetime';

export type BirthDatePart = number | string | null | undefined;

export interface BirthDateParts {
	year: BirthDatePart;
	month: BirthDatePart;
	day: BirthDatePart;
}

/** A birth date as numbers, any part of it possibly missing. */
export interface BirthDateNumbers {
	year?: number;
	month?: number;
	day?: number;
}

export interface BirthDateBound {
	min: number;
	max: number;
}

export interface BirthDateBounds {
	year: BirthDateBound;
	month: BirthDateBound;
	day: BirthDateBound;
}

export const MIN_BIRTH_YEAR = 1900;

// Stands in for an empty or partly typed year. A leap year, so 29 February is
// only refused once a full non-leap year is typed
const LEAP_YEAR_FALLBACK = 2000;

// The sites store `Number(event.target.value)`, so a cleared box arrives as 0
function toNumber(part: BirthDatePart): number | null {
	if (part === null || part === undefined || part === '') {
		return null;
	}

	const value = typeof part === 'number' ? part : Number.parseInt(part, 10);

	if (Number.isNaN(value) || value === 0) {
		return null;
	}

	return value;
}

// "2" and "201" are a year still being typed, not years 2 and 201
function toYear(part: BirthDatePart): number | null {
	const value = toNumber(part);

	if (value === null || value < MIN_BIRTH_YEAR) {
		return null;
	}

	return value;
}

export function birthDatePartValue(part: BirthDatePart): string {
	if (toNumber(part) === null) {
		return '';
	}

	return String(part);
}

type BirthDateBoundParts = Pick<BirthDateParts, 'year' | 'month'>;

export function birthDateBounds({
	year,
	month,
}: BirthDateBoundParts): BirthDateBounds {
	// An empty month reads as January, and getDaysInMonth clamps one outside 1 to 12
	const maxDay = DateTime.getDaysInMonth(
		toYear(year) ?? LEAP_YEAR_FALLBACK,
		toNumber(month) ?? 1
	);

	return {
		day: { min: 1, max: maxDay },
		month: { min: 1, max: 12 },
		year: { min: MIN_BIRTH_YEAR, max: new Date().getFullYear() },
	};
}

export function isImpossibleBirthDate({ year, month, day }: BirthDateParts) {
	const y = toYear(year);
	const m = toNumber(month);
	const d = toNumber(day);

	if (y === null || m === null || d === null) {
		return false;
	}

	return !DateTime.isValidISODateString(`${y}-${m}-${d}`);
}

export function impossibleBirthDateMessage(): string {
	return t('This date does not exist');
}

/**
 *	A `YYYY-MM-DD` birth date, or an ISO timestamp starting with one, as its
 *	parts. Read from the string rather than through `Date`, which can move it
 *	a day in a time zone west of UTC.
 */
export function birthDateParts(birthDate?: string | null): BirthDateNumbers {
	const [year, month, day] = (birthDate ?? '')
		.slice(0, 10)
		.split('-')
		.map((part) => Number.parseInt(part, 10));

	if (!year || !month || !day) {
		return {};
	}

	return { year, month, day };
}

/** Birth date parts as the API takes them, `YYYY-MM-DD`. */
export function birthDateValue({ year, month, day }: BirthDateNumbers): string {
	return [year, month, day]
		.map((part) => (part ? part.toString() : ''))
		.map((part) => part.padStart(2, '0'))
		.join('-');
}
